import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import {
  buildProcessData,
  buildPrecursorData,
  PRODUCT_COLS,
} from "./exportSummaryExcel";

const MANUAL_COLS = new Set([5, 13,14,15,16,17,18,19,20,21,22,23,24,25,26,27,28,29,30,31,32,33,34,40,41]);

const TEAL = [55, 99, 102];

const safe = (v) => {
  if (v === null || v === undefined || v === "") return "";
  return String(v);
};

const fmtN = (v, d = 6) => {
  const n = Number(v);
  return Number.isFinite(n) && n !== 0 ? parseFloat(n.toFixed(d)) : "";
};

const fmtDate = (v) => {
  if (!v) return "";
  try {
    return new Date(v).toLocaleDateString("en-GB");
  } catch {
    return String(v);
  }
};

const title = (doc, text) => {
  doc.setFontSize(20);
  doc.setFont("helvetica", "bold");
  doc.text(text, 148, 15, { align: "center" });

  doc.setFontSize(9.5);
  doc.setTextColor(100, 100, 100);
  doc.setFont("helvetica", "normal");
  doc.text("Note: Blank cells indicate fields not entered or not applicable.", 14, 24);
  doc.setTextColor(0, 0, 0);
};

const section = (doc, text, y) => {
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(text, 14, y);
  return y + 6;
};

const table = (doc, y, head, body, fontSize = 9) => {
  autoTable(doc, {
    startY: y,
    head: [head],
    body,
    theme: "grid",
    styles: {
      fontSize,
      cellPadding: 1.5,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: TEAL,
      textColor: 255,
      fontStyle: "bold",
    },
    margin: { left: 8, right: 8 },
  });

  return doc.lastAutoTable.finalY + 10;
};

const pageIfNeeded = (doc, y) => {
  if (y > 185) {
    doc.addPage();
    title(doc, "CBAM Summary Report");
    return 36;
  }
  return y;
};

const addFooter = (doc) => {
  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.setFontSize(8);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100, 100, 100);

    doc.text(
      `Page ${i} of ${pageCount}`,
      pageWidth - 20,
      pageHeight - 8,
      { align: "right" }
    );

    doc.text(
      "Generated by OV Audit",
      14,
      pageHeight - 8
    );

    doc.setTextColor(0, 0, 0);
  }
};

const getProductValue = (colNo, rowData, isProc, manual = {}) => {
  if (MANUAL_COLS.has(colNo)) return manual[colNo] || "";
  switch (colNo) {
    case 1:
      return rowData.id;
    case 2:
      return rowData.goodsCat || rowData.name || "";
    case 3:
      return rowData.cnCode || "";
    case 4:
      return rowData.cnName || "";
    case 6:
      return fmtN(rowData.seeDirect ?? rowData.see_direct);
    case 7:
      return fmtN(rowData.seeIndirect ?? rowData.see_indirect);
    case 8:
      return fmtN(rowData.seeTotal ?? rowData.see_total);
    case 9:
      return "t";
    case 10:
      return isProc ? "" : rowData.isDefault ? "TRUE" : "FALSE";
    case 11:
      return rowData.elecSrc || "";
    case 12:
      return fmtN(rowData.elecPerT);
    case 35:
      return rowData.cpInstrument || "";
    case 36:
      return fmtN(rowData.shareEmCovered);
    case 37:
      return fmtN(isProc ? rowData.shareEmCovered * (rowData.see_total || 0) : "");
    case 38:
      return rowData.currency || "EUR";
    case 39:
      return fmtN(rowData.cpDue);
    case 42:
      return fmtN(isProc ? rowData.rebate * (rowData.see_total || 0) : "");
    case 43:
      return fmtN(rowData.rebate);
    case 44:
      return fmtN(rowData.effectiveCp);
    default:
      return "";
  }
};

export const exportSummaryPdf = (allFormData, manualData = {}, { returnBlob = false, status = '' } = {}) => {
  const doc = new jsPDF("landscape", "mm", "a4");

  const fA = allFormData?.formA || {};
  const fC = allFormData?.formC || {};

  const processData = buildProcessData(allFormData);
  const precursorData = buildPrecursorData(allFormData);

  const aggGoods = fA.aggregatedGoods || [];
  const relevantProcesses = fA.relevantProcesses || [];
  const purchasedPrecursors = fA.purchasedPrecursors || [];

  const calcBased = Number(fC?.ghgBalanceByMethod?.calculationBased) || 0;
  const pfcTotal = Number(fC?.ghgBalanceByMethod?.totalPFC) || 0;
  const measBased = Number(fC?.ghgBalanceByMethod?.measurementBased) || 0;
  const otherEm = Number(fC?.ghgBalanceByMethod?.other) || 0;
  const totalDirect = Number(fC?._totals?.directEmissions || 0);
  const totalIndirect = Number(fC?._totals?.indirectEmissions || 0);
  const totalAll = Number(fC?._totals?.grandTotalCO2e || totalDirect + totalIndirect);

  title(doc, "CBAM Summary Report");

  let y = 36;

  // ====================================================
  // 1. SUMMARY PROCESSES
  // ====================================================
  y = section(doc, "1. Summary Processes", y);

  y = table(
    doc,
    y,
    ["Parameter", "Value"],
    [
      ["Name of the manufacturing facility (English name)", safe(fA.installationNameEnglish || fA.installationName)],
      ["Street, Number", safe(fA.street)],
      ["Economic activity", safe(fA.economicActivity)],
      ["City", safe(fA.city)],
      ["Country", safe(fA.country)],
      ["UNLOCODE", safe(fA.unLocode)],
      ["Coordinates latitude", safe(fA.latitude)],
      ["Coordinates longitude", safe(fA.longitude)],
      ["Reporting period start", fmtDate(fA.reportingPeriodStart)],
      ["Reporting period end", fmtDate(fA.reportingPeriodEnd)],
      ["Carbon price instrument", safe(fA.carbonPriceInstrument)],
      ["Any additional information", safe(fA.additionalInfo)],
    ],
    10
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.1 Aggregated Goods", y);

  y = table(
    doc,
    y,
    ["ID", "Aggregated good produced", "Routes", "Route 1", "Route 2", "Route 3", "Route 4", "Route 5", "Route 6"],
    aggGoods.map((g) => [
      safe(g.id),
      safe(g.category),
      safe(g.primaryRoute || "All production routes"),
      safe(g.route1),
      safe(g.route2),
      safe(g.route3),
      safe(g.route4),
      safe(g.route5),
      safe(g.route6),
    ]),
    9
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.2 Production Processes", y);

  y = table(
    doc,
    y,
    ["ID", "Production process", "Aggregated goods category", "1", "2", "3", "4", "5", "6"],
    relevantProcesses.map((p) => [
      safe(p.id),
      safe(p.name),
      safe(p.aggregatedGoodsCategory),
      safe(p.inc1),
      safe(p.inc2),
      safe(p.inc3),
      safe(p.inc4),
      safe(p.inc5),
      safe(p.inc6),
    ]),
    9
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.3 Purchased Precursors", y);

  y = table(
    doc,
    y,
    ["ID", "Purchased precursor", "Aggregated goods category", "Route 1", "Route 2", "Route 3", "Route 4", "Route 5", "Country code"],
    purchasedPrecursors.map((p) => [
      safe(p.id),
      safe(p.name),
      safe(p.linkedProcessId),
      safe(p.route1),
      safe(p.route2),
      safe(p.route3),
      safe(p.route4),
      safe(p.route5),
      safe(p.countryCode),
    ]),
    9
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.4 GHG Emissions Balance", y);

  y = table(
    doc,
    y,
    [
      "Process",
      "Production process",
      "Aggregated goods category",
      "Unit",
      "DirEm",
      "Heat emissions",
      "Waste gas emissions",
      "Total direct",
      "Indirect",
      "Activity level",
      "Unit",
    ],
    processData.map((p) => [
      safe(p.id),
      safe(p.name),
      safe(p.goodsCat),
      "tCO2e",
      fmtN(p.dirEm),
      fmtN(p.heatEm),
      fmtN(p.wgEm),
      fmtN(p.totalDirect),
      fmtN(p.indirect),
      fmtN(p.activityLevel, 0),
      safe(p.unit),
    ]),
    8.5
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.5 Comparison with Form C", y);

  const sumDirect = processData.reduce((s, p) => s + Number(p.totalDirect || 0), 0);
  const sumIndirect = processData.reduce((s, p) => s + Number(p.indirect || 0), 0);

  y = table(
    doc,
    y,
    ["Comparison", "Unit", "Total direct emissions", "Total indirect emissions"],
    [
      ["Total emissions of the manufacturing facility from Form C", "tCO2e", fmtN(totalDirect), fmtN(totalIndirect)],
      ["Rest = emissions not attributed to CBAM processes", "tCO2e", fmtN(totalDirect - sumDirect), fmtN(totalIndirect - sumIndirect)],
      ["Rest expressed as % of total", "-", totalDirect ? `${((totalDirect - sumDirect) / totalDirect * 100).toFixed(2)}%` : "", totalIndirect ? `${((totalIndirect - sumIndirect) / totalIndirect * 100).toFixed(2)}%` : ""],
    ],
    10
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.6 Specific Direct and Indirect Embedded Emissions", y);

  y = table(
    doc,
    y,
    [
      "Process",
      "Production process",
      "Aggregated goods category",
      "SE direct",
      "SEE direct",
      "SE indirect",
      "SEE indirect",
      "Elec consumed MWh/t",
      "Embedded electricity MWh/t",
      "CP due",
      "Rebate",
    ],
    processData.map((p) => [
      safe(p.id),
      safe(p.name),
      safe(p.goodsCat),
      fmtN(p.see_direct),
      fmtN(p.see_direct),
      fmtN(p.see_indirect),
      fmtN(p.see_indirect),
      fmtN(p.elecPerT),
      fmtN(p.elecPerT),
      fmtN(p.cpDue),
      fmtN(p.rebate),
    ]),
    8.5
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.7 Purchased Precursor SEE", y);

  y = table(
    doc,
    y,
    [
      "ID",
      "Purchased precursor",
      "Type of precursor",
      "SEE direct",
      "SEE indirect",
      "Electricity consumed MWh/t",
    ],
    precursorData.map((p) => [
      safe(p.id),
      safe(p.name),
      safe(p.goodsType),
      fmtN(p.seeDirect),
      fmtN(p.seeIndirect),
      fmtN(p.elecPerT),
    ]),
    7
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "1.8 Detailed Overview of Each Production Process", y);

  processData.forEach((p) => {
    y = pageIfNeeded(doc, y);

    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text(`${p.id} — ${p.name} | ${p.goodsCat}`, 14, y);
    y += 4;

    const embDirect = Number(p.see_direct || 0) * Number(p.activityLevel || 0);
    const embIndirect = Number(p.see_indirect || 0) * Number(p.activityLevel || 0);
    const embTotal = embDirect + embIndirect;

    y = table(
      doc,
      y,
      [
        "Row",
        "Aggregated goods category",
        "Mass share",
        "Default value",
        "SEE direct",
        "SEE indirect",
        "SEE total",
        "EmbEm direct",
        "EmbEm indirect",
        "EmbEm total",
        "Source Electricity EF",
        "Embedded electricity",
        "Country code",
        "CP due",
        "Rebate",
      ],
      [[
        "Total",
        safe(p.goodsCat),
        "",
        "",
        fmtN(p.see_direct),
        fmtN(p.see_indirect),
        fmtN(p.see_direct + p.see_indirect),
        fmtN(embDirect),
        fmtN(embIndirect),
        fmtN(embTotal),
        "",
        fmtN(p.elecPerT),
        "",
        fmtN(p.cpDue),
        fmtN(p.rebate),
      ]],
      7.5
    );

    const consumedPrecs =
  p._proc?.consumedOtherProcesses ||
  p._proc?.consumedInProcesses ||
  [];

const ePrecursors = allFormData?.formE?.precursors || [];
const fAPrecursors = allFormData?.formA?.purchasedPrecursors || [];

if (consumedPrecs.length > 0) {
  y = table(
    doc,
    y,
    [
      "Row",
      "Aggregated goods category",
      "Mass share",
      "Default value",
      "SEE direct",
      "SEE indirect",
      "SEE total",
      "EmbEm direct",
      "EmbEm indirect",
      "EmbEm total",
      "Source Electricity EF",
      "Embedded electricity",
      "Country code",
      "CP due",
      "Rebate",
    ],
    consumedPrecs
    .filter((cp2) => cp2.precursorId || cp2.amounts)
    .map((cp2) => {
      const pp = fAPrecursors.find((p) => p.id === cp2.precursorId) || {};
      const ep = ePrecursors.find((e) => e.id === cp2.precursorId) || {};

      const qty = Number(cp2.amounts || 0);
      const massShare =
        Number(p.activityLevel || 0) > 0
          ? qty / Number(p.activityLevel || 0)
          : 0;

      const cpSeeDirect = Number(ep.emissions?.seeDirect || 0) * massShare;
      const cpSeeIndirect = Number(ep.emissions?.seeIndirect || 0) * massShare;

      const cpEmbDirect = cpSeeDirect * Number(p.activityLevel || 0);
      const cpEmbIndirect = cpSeeIndirect * Number(p.activityLevel || 0);
      const isDefault = ep.emissions?.sourceSeeDirect === "Default";

      return [
        safe(pp.id || cp2.precursorId),
        safe(pp.name),
        fmtN(massShare),
        isDefault ? "TRUE" : "FALSE",
        fmtN(cpSeeDirect),
        fmtN(cpSeeIndirect),
        fmtN(cpSeeDirect + cpSeeIndirect),
        fmtN(cpEmbDirect),
        fmtN(cpEmbIndirect),
        fmtN(cpEmbDirect + cpEmbIndirect),
        safe(ep.emissions?.eefIndirectSource),
        fmtN(Number(ep.emissions?.secIndirect || 0) * massShare),
        safe(pp.countryCode),
        "",
        "",
      ];
    }),
    7.5
  );
}
  });

  // ====================================================
  // 2. SUMMARY PRODUCTS — 44 COLUMNS, SPLIT INTO GROUPS
  // ====================================================
  doc.addPage();
  title(doc, "CBAM Summary Report");
  y = 36;
  y = section(doc, "2. Summary Products", y);

  const allProductRows = [
    ...processData.map((row) => ({ row, isProc: true })),
    ...precursorData.map((row) => ({ row, isProc: false })),
  ];

  const productGroups = [
    { title: "2.1 Product Identification", from: 1, to: 5 },
    { title: "2.2 SEE and Electricity", from: 6, to: 12 },
    { title: "2.3 Additional Product Parameters", from: 13, to: 34 },
    { title: "2.4 Carbon Pricing", from: 35, to: 44 },
  ];

  productGroups.forEach((group) => {
    y = pageIfNeeded(doc, y);
    y = section(doc, group.title, y);

    const cols = PRODUCT_COLS.filter((c) => c.n >= group.from && c.n <= group.to);

    y = table(
      doc,
      y,
      cols.map((c) => `[${c.n}] ${c.label.replace(/\n/g, " ")}`),
      allProductRows.map(({ row, isProc }) =>
        cols.map((c) => safe(getProductValue(
          c.n, row, isProc,
          isProc ? (manualData[`proc-${row.id}`] || {}) : {}
        )))
      ),
      group.to - group.from > 12 ? 7 : 8.5
    );
  });

  // ====================================================
  // 3. SUMMARY COMMUNICATION
  // ====================================================
  doc.addPage();
  title(doc, "CBAM Summary Report");
  y = 36;
  y = section(doc, "3. Summary Communication", y);

  y = table(
    doc,
    y,
    ["Parameter", "Value"],
    [
      ["Name of the manufacturing facility (English name)", safe(fA.installationNameEnglish || fA.installationName)],
      ["Street, Number", safe(fA.street)],
      ["Economic activity", safe(fA.economicActivity)],
      ["Country", safe(fA.country)],
      ["UNLOCODE", safe(fA.unLocode)],
      ["Coordinates latitude", safe(fA.latitude)],
      ["Coordinates longitude", safe(fA.longitude)],
      ["Reporting period start", fmtDate(fA.reportingPeriodStart)],
      ["Reporting period end", fmtDate(fA.reportingPeriodEnd)],
      ["Calculation-based excluding PFC", fmtN(calcBased)],
      ["Total PFC emissions", fmtN(pfcTotal)],
      ["Measurement-based", fmtN(measBased)],
      ["Other", fmtN(otherEm)],
      ["Total direct emissions", fmtN(totalDirect)],
      ["Total indirect emissions", fmtN(totalIndirect)],
      ["Total emissions", fmtN(totalAll)],
      ["General data quality", safe(fC?.dataQuality?.generalInfo)],
      ["Justification", safe(fC?.dataQuality?.justification)],
      ["Assurance", safe(fC?.dataQuality?.assurance)],
      ["Carbon price instrument", safe(fA.carbonPriceInstrument)],
      ["Additional information", safe(fA.additionalInfo)],
    ],
    8
  );

  y = pageIfNeeded(doc, y);
  y = section(doc, "3.1 Communication Product Table", y);

  productGroups.forEach((group) => {
    y = pageIfNeeded(doc, y);
    y = section(doc, group.title.replace("2.", "3."), y);

    const cols = PRODUCT_COLS.filter((c) => c.n >= group.from && c.n <= group.to);

    y = table(
      doc,
      y,
      cols.map((c) => `[${c.n}] ${c.label.replace(/\n/g, " ")}`),
     allProductRows.map(({ row, isProc }) =>
        cols.map((c) => safe(getProductValue(
          c.n, row, isProc,
          isProc ? (manualData[`proc-${row.id}`] || {}) : {}
        )))
      ),
      group.to - group.from > 12 ? 7 : 8.5
    );
  });

  addFooter(doc);

  const watermarkText =
    status === 'submitted' ? 'Submitted' :
    status === 'published' ? 'Published' :
    '';

  addWatermark(doc, watermarkText);

  if (returnBlob) {
    return doc.output("blob");
  }

  doc.save("CBAM_Summary_Report.pdf");
};

  const addWatermark = (doc, text) => {
  if (!text) return;

  const pageCount = doc.internal.getNumberOfPages();

  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    doc.saveGraphicsState();
    doc.setGState(new doc.GState({ opacity: 0.25 }));
    doc.setFontSize(60);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(150, 150, 150);

    doc.text(text.toUpperCase(), pageWidth / 2, pageHeight / 2, {
      align: "center",
      baseline: "middle",
      angle: 30,
    });

    doc.restoreGraphicsState();
    doc.setTextColor(0, 0, 0);
  }
};